import { useState } from "react";
import { ToolContainer } from "@/components/ToolLayout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Download, Eye, EyeOff } from "lucide-react";
import { generateLegalDoc, generateLegalDocPdf, today, type DocSection } from "./legalDocUtils";
import { LegalDocPreview } from "./LegalDocPreview";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogUsageOnAction } from "@/hooks/useLogUsageOnAction";
import { LegalField } from "./LegalField";
import { amountWithWords, positiveAmountMessage } from "./amountWords";
import { parseNumber } from "@/tools/converters/numberInput";
import { NoticeBox } from "@/components/NoticeBox";

const TOOL_INFO_CONTENT = {
  what: `Generates a bill of sale document for transferring ownership of an item (like a vehicle or equipment) between a buyer and seller.`,
  how: `Fill in the buyer, seller, item details, and sale price. Preview the document, then download it as a Word or PDF file.`,
  tip: `For a vehicle sale specifically, check whether your state requires a particular official form in addition to (or instead of) a general bill of sale \u2014 vehicle title transfers often have their own specific paperwork requirements.`,
};

const TITLE = "Bill of Sale";

export default function BillOfSaleGenerator() {
  const [seller, setSeller] = useState("");
  const [buyer, setBuyer] = useState("");
  const [itemDescription, setItemDescription] = useState("");
  const [price, setPrice] = useState("");
  const [state, setState] = useState("");
  const [sellerNotes, setSellerNotes] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [isVehicle, setIsVehicle] = useState(false);
  const [vin, setVin] = useState("");
  const [odometer, setOdometer] = useState("");
  const [odoStatus, setOdoStatus] = useState<"actual" | "exceeds" | "not-actual">("actual");
  const [plate, setPlate] = useState("");

  // The price is typed as text so "1,500.50" and "$1500" work; it is read with parseNumber and shown back with separators.
  const priceNum = parseNumber(price);
  const priceMsg = positiveAmountMessage(price, priceNum, "Price");

  // Seller/buyer/item/price are the four the terms of sale can't
  // meaningfully exist without -- state and seller's notes stay optional.
  const vinOk = /^[A-HJ-NPR-Z0-9]{17}$/i.test(vin.trim()); // 17 chars, no I, O or Q
  const missingRequired =
    !seller.trim() || !buyer.trim() || !itemDescription.trim() || !price.trim() || !!priceMsg || priceNum === null ||
    (isVehicle && (!vinOk || !odometer.trim()));

  const odoText = {
    actual: "reflects the actual mileage of the vehicle",
    exceeds: "EXCEEDS the mechanical limits of the odometer",
    "not-actual": "is NOT the actual mileage (WARNING: ODOMETER DISCREPANCY)",
  }[odoStatus];

  const vehicleSections = (): DocSection[] =>
    isVehicle
      ? [
          {
            heading: "Vehicle Details and Odometer Disclosure",
            paragraphs: [
              `Vehicle Identification Number (VIN): ${vin.trim().toUpperCase()}${plate.trim() ? `; License plate: ${plate.trim()}` : ""}.`,
              `Odometer reading at time of sale: ${odometer.trim()} miles. Seller states that, to the best of Seller's knowledge, the odometer reading ${odoText}.`,
              "Seller will sign over the certificate of title to Buyer at the time of sale. Buyer is responsible for title transfer, registration and any taxes or fees required by the state.",
            ],
          },
        ]
      : [];

  const buildSections = (): DocSection[] => [
    {
      paragraphs: [
        `This Bill of Sale is made on ${today()}, between ${seller || "[Seller Name]"} ("Seller") and ${buyer || "[Buyer Name]"} ("Buyer").`,
      ],
    },
    {
      heading: "1. Item Sold",
      paragraphs: [`Seller agrees to sell, and Buyer agrees to purchase, the following item(s): ${itemDescription || "[Item Description]"}.`],
    },
    {
      heading: "2. Purchase Price",
      paragraphs: [`The total purchase price is ${priceNum !== null && priceNum > 0 ? amountWithWords(priceNum) : "[Amount]"}, paid in full by Buyer to Seller upon signing of this document.`],
    },
    {
      heading: "3. Condition",
      paragraphs: sellerNotes.trim()
        ? ["The item is sold \"as-is,\" with no warranties expressed or implied, unless otherwise noted by the Seller in writing.", `Seller's notes: ${sellerNotes.trim()}`]
        : ["The item is sold \"as-is,\" with no warranties expressed or implied, unless otherwise noted by the Seller in writing."],
    },
    {
      heading: "4. Ownership Transfer",
      paragraphs: ["Seller confirms they are the rightful owner of the item and have the right to sell it, free of any liens or encumbrances, unless otherwise disclosed."],
    },
    ...vehicleSections(),
    {
      heading: "5. Governing Law",
      paragraphs: [`This Bill of Sale is governed by the laws of ${state || "[State]"}.`],
    },
    {
      heading: "Signatures",
      paragraphs: ["Seller: _________________________  Date: _____________", "Buyer: _________________________  Date: _____________"],
    },
  ];

  // Was useLogToolUsage(true, 3000, { ownActions: true }) -- a debounced logger with no
  // gating at all, while the real value-delivering actions (the Word/
  // PDF download buttons below) had ZERO usage-limit check of any
  // kind, not even the weak kind -- confirmed directly: clicking
  // either button at the daily limit generated and downloaded a real,
  // complete document. logUsageNow() gates each button's own click
  // directly, same pattern as Merge PDF's own discrete action.
  const { logUsageNow } = useLogUsageOnAction();


  return (
    <ToolContainer>
        <div className="max-w-md mx-auto space-y-4">
          <LegalField label="Seller Name *"><Input value={seller} onChange={e => setSeller(e.target.value)} /></LegalField>
          <LegalField label="Buyer Name *"><Input value={buyer} onChange={e => setBuyer(e.target.value)} /></LegalField>
          <label className="flex items-center gap-3 text-sm cursor-pointer min-h-11"><input type="checkbox" className="h-5 w-5 shrink-0" checked={isVehicle} onChange={e => setIsVehicle(e.target.checked)} /> This is a motor vehicle (adds VIN and odometer disclosure)</label>
          {isVehicle && (
            <div className="space-y-3 rounded-lg border p-3">
              <LegalField label="VIN * (17 characters)" error={vin && !vinOk ? "A VIN has 17 letters and digits (no I, O or Q)." : null}><Input value={vin} onChange={e => setVin(e.target.value.toUpperCase())} maxLength={17} className="font-mono" /></LegalField>
              <LegalField label="Odometer (miles) *"><Input inputMode="numeric" value={odometer} onChange={e => setOdometer(e.target.value.replace(/[^0-9]/g, ""))} /></LegalField>
              <LegalField label="Odometer statement">
                <select className="w-full border border-border rounded-md bg-background px-2 py-2 text-base sm:text-sm" value={odoStatus} onChange={e => setOdoStatus(e.target.value as typeof odoStatus)}>
                  <option value="actual">Reflects the actual mileage</option>
                  <option value="exceeds">Exceeds the mechanical limits</option>
                  <option value="not-actual">Not the actual mileage</option>
                </select>
              </LegalField>
              <LegalField label="License plate (optional)"><Input value={plate} onChange={e => setPlate(e.target.value)} /></LegalField>
            </div>
          )}
          <LegalField label="Item Description *"><Textarea value={itemDescription} onChange={e => setItemDescription(e.target.value)} placeholder="e.g. 2019 Honda Civic, VIN: ..." className="min-h-[80px]" /></LegalField>
          <LegalField label="Purchase Price ($) *" error={priceMsg} hint={priceNum !== null && !priceMsg ? amountWithWords(priceNum) : undefined}><Input type="text" inputMode="decimal" value={price} onChange={e => setPrice(e.target.value)} placeholder="1,500.00" /></LegalField>
          <LegalField label="State (optional)"><Input value={state} onChange={e => setState(e.target.value)} /></LegalField>
          <LegalField label="Seller's Notes (optional)"><Textarea value={sellerNotes} onChange={e => setSellerNotes(e.target.value)} placeholder="Any additional notes about the item's condition, known issues, or terms" className="min-h-[80px]" /></LegalField>
          {missingRequired && (
            <NoticeBox className="text-xs">Seller, buyer, item description, and price are required before you can preview or download.</NoticeBox>
          )}

          <Button variant="outline" onClick={() => setShowPreview(v => !v)} disabled={missingRequired} className="w-full gap-2">
            {showPreview ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />} {showPreview ? "Hide Preview" : "Preview"}
          </Button>

          {showPreview && !missingRequired && (
            <LegalDocPreview title={TITLE} sections={buildSections()} />
          )}

          <div className="flex gap-2">
            <Button onClick={async () => { const allowed = await logUsageNow(); if (!allowed) return; generateLegalDoc(TITLE, buildSections(), "bill-of-sale.docx"); }} disabled={missingRequired} className="flex-1 gap-2">
              <Download className="h-4 w-4" /> Word
            </Button>
            <Button onClick={async () => { const allowed = await logUsageNow(); if (!allowed) return; generateLegalDocPdf(TITLE, buildSections(), "bill-of-sale.pdf"); }} disabled={missingRequired} variant="secondary" className="flex-1 gap-2">
              <Download className="h-4 w-4" /> PDF
            </Button>
          </div>
        </div>
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} disclaimer="This tool generates a general-purpose bill of sale template. It isn't legal advice and may not account for the specific laws of your state, country, or situation — requirements for this type of bill of sale can vary significantly by jurisdiction. Have any document reviewed by a qualified attorney before you sign it or rely on it, especially for anything involving significant money, property, or legal obligations." />
    </ToolContainer>
  );
}
